import React from 'react';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import BasicLayout from 'layout/Basic';
import { Row, Divider, Col } from 'antd';
import styled from 'styled-components';
import parse from 'html-react-parser';
import NImage from 'next/image';
import Image from 'components/Image';
import { convertProps } from 'lib/parseAPIResponse';
import { SubTitle } from 'components/PageTitle';
import Avatar from 'components/Avatar';
import Newsletter from 'containers/Newsletter';
import { SkyscraperMobile, Wideboard } from 'components/Ads';
import Section from 'components/Section';
import WebsiteImage from '/public/static/images/website.jpg';
import { merriweather } from 'styles/GlobalStyle';
import LogoHeadingSection from 'components/LogoHeadingSection';

const PersonName = styled.h2`
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: right;
    padding-top: 15px;
    margin: 0;
    a {
        color: ${({ theme }) => theme.colors.greyishBrown};
    }
`;

const PersonContainer = styled.div`
    text-align: right;
    word-wrap: break-word;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const PersonRow = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    margin: 0;
    a {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const SpecificationsTitle = styled.h3`
    font-size: 32px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.31;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
    margin: 0;
`;

const SpecificationsDescription = styled.h3`
    font-family: ${merriweather.style.fontFamily};
    font-size: 24px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
    margin: 0;
`;

const DownloadImageContainer = styled.div`
    width: 100%;
    background: #b7cbd9;
    > a > img {
        margin: 0 auto !important;
        display: block !important;
    }
`;

const IssueContainer = styled(Col)``;

const PageTitle = styled.h1`
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    font-size: 32px;
    font-weight: bold;
    margin-bottom: 0px;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const SpecsHeading = styled.span`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const SpecsTextTitle = styled.p`
    margin-top: 1em;

    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const SpecsTextDescription = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const NameCol = styled(Col)`
    @media (max-width: 992px) {
        margin-top: 25px;
    }
`;

const StyledDivider = styled(Divider)`
    @media (min-width: 992px) {
        margin-top: 100px;
    }
`;

interface Props {
    contact: Contact;
    latestIssue: Issue;
    specification: MagazineSpecification;
    ads: PageAds;
}
export const AboutPage = ({ contact, latestIssue, specification, ads }: Props): JSX.Element => {
    const { name } = getConfig().publicRuntimeConfig;
    const { skyscraper, wideboardBottom } = ads;
    return (
        <BasicLayout ads={ads}>
            <LogoHeadingSection />
            <Section>
                <NameCol lg={{ span: 17, offset: 7 }} xs={24}>
                    <PageTitle>{name}</PageTitle>
                </NameCol>
            </Section>
            <StyledDivider />
            <Row gutter={16}>
                <Col xs={24} xl={6}>
                    <Row>
                        <Col span={24}>
                            <SubTitle>Czasopismo</SubTitle>
                        </Col>
                        <IssueContainer span={24}>
                            <Image
                                type="issue"
                                alt="Czasopismo"
                                src={latestIssue.cover}
                                width={272}
                                height={389}
                            />
                        </IssueContainer>
                    </Row>
                </Col>
                <Col xs={24} md={12} lg={0}>
                    <SkyscraperMobile id={skyscraper[0]} margin="25px auto 0 auto" />
                </Col>
                <Col xs={24} md={12} lg={0}>
                    <SkyscraperMobile id={skyscraper[1]} margin="25px auto 0 auto" />
                </Col>
                <Col xs={24} md={12}>
                    <Section gutter={[16, 32]}>
                        <Col span={24}>
                            <SpecsHeading>
                                Poniżej znajdziesz specyfikacje techniczne reklam w czasopiśmie oraz
                                na naszej stronie internetowej. Zachęcamy do pobrania plików.
                            </SpecsHeading>
                        </Col>
                        <Col xs={24} md={12}>
                            <DownloadImageContainer>
                                <a href={specification.specificationForPaper}>
                                    <Image
                                        type="issue"
                                        src={latestIssue.cover}
                                        alt="cover"
                                        width={107}
                                        height={153}
                                    />
                                </a>
                            </DownloadImageContainer>
                            <SpecsTextTitle>Czasopismo</SpecsTextTitle>
                            <SpecsTextDescription>
                                Specyfikacja techniczna reklam publikowanych w papierowym wydaniu
                                czasopisma.
                            </SpecsTextDescription>
                        </Col>
                        <Col xs={24} md={12}>
                            <DownloadImageContainer>
                                <a href={specification.specificationForWebsite}>
                                    <NImage
                                        src={WebsiteImage}
                                        alt="website"
                                        width="283"
                                        height="153"
                                    />
                                </a>
                            </DownloadImageContainer>
                            <SpecsTextTitle>Strona WWW</SpecsTextTitle>
                            <SpecsTextDescription>
                                Specyfikacja techniczna reklam publikowanych na stronie
                                internetowej.
                            </SpecsTextDescription>
                        </Col>
                    </Section>
                </Col>
                <Col xs={24} md={6}>
                    <SpecificationsTitle>Nakład</SpecificationsTitle>
                    <SpecificationsDescription>
                        {specification.printedCirculation}
                    </SpecificationsDescription>
                    <Divider />
                    <SpecificationsTitle>Liczba wydań</SpecificationsTitle>
                    <SpecificationsDescription>{specification.frequency}</SpecificationsDescription>
                    <Divider />
                    <SpecificationsTitle>Wymiary</SpecificationsTitle>
                    <SpecificationsDescription>
                        {parse(specification.sizeOfMagazine)}
                    </SpecificationsDescription>
                    <Divider />
                    <SpecificationsTitle>Obszar zadruku</SpecificationsTitle>
                    <SpecificationsDescription>{specification.typeArea}</SpecificationsDescription>
                </Col>
            </Row>
            <Section>
                <Col span={24}>
                    <Divider />
                </Col>
            </Section>
            <Section gutter={16} $marginTop={25}>
                <Col xs={24} md={6}>
                    <SubTitle>DZIAŁ REKLAMY</SubTitle>
                </Col>
                <Col xs={24} md={18}>
                    <Row gutter={[16, 32]}>
                        {contact.advertisementContacts.map((person) => {
                            return (
                                <Col
                                    xs={24}
                                    md={12}
                                    xl={8}
                                    key={`${person.firstName}-${person.lastName}`}
                                >
                                    <PersonContainer>
                                        <Avatar person={person} size={90} />
                                        <PersonName>{`${person.firstName} ${person.lastName}`}</PersonName>
                                        <PersonRow>{person.jobTitle}</PersonRow>
                                        <PersonRow style={{ marginBottom: 20 }}>
                                            {person.department}
                                        </PersonRow>
                                        {person.phone1 && (
                                            <PersonRow>tel: {person.phone1}</PersonRow>
                                        )}
                                        {person.phone2 && (
                                            <PersonRow>tel: {person.phone2}</PersonRow>
                                        )}
                                        <PersonRow>
                                            <a
                                                href={`mailto:${person.email}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {parse(person.email.replace('@', '&#8203;@'))}
                                            </a>
                                        </PersonRow>
                                    </PersonContainer>
                                </Col>
                            );
                        })}
                    </Row>
                </Col>
            </Section>
            <Section gutter={[16, 32]} justify="center">
                <Col span={24}>
                    <Divider />
                </Col>
                <Col xs={24} md={20}>
                    <Newsletter />
                </Col>
                <Col span={24}>
                    <Divider />
                </Col>
            </Section>
            <Wideboard id={wideboardBottom[0]} />
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async () => {
    const { apiUrl } = getConfig().publicRuntimeConfig;
    return Promise.all([
        fetch(`${apiUrl}/public/api/about/pl`),
        fetch(`${apiUrl}/public/api/naszadsik?path=reklama`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();
        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default AboutPage;
